import type { QuoteCarousel, QuoteSlide } from './types';
import type { CropCenterKeyframeLike } from '@/lib/cover/resolveCropCenterXAtTime';
import { resolvePreviewVideoUrl } from '@/lib/preview/resolvePreviewVideoUrl';

/** Every slide whose background is an uncaptured frame (`{ kind: 'frame',
 *  url: null }`) — regardless of the carousel style: a card carousel can
 *  carry per-slide frame backgrounds too. */
export function slidesNeedingFrameCapture(carousel: QuoteCarousel | null): QuoteSlide[] {
  if (!carousel) return [];
  return carousel.slides.filter(
    (slide) => slide.background?.kind === 'frame' && slide.background.url === null,
  );
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * The clip's kept intervals (source axis), in playback order — the scene
 * order the export renders (`clip.data.segments` array order). Clips without
 * segments fall back to the single `clip_start_time..clip_end_time` range.
 * Only the EDITED source needs them (to map onto the export timeline).
 */
export function clipCaptureIntervals(args: {
  segments: unknown;
  clipStartTime: number | null | undefined;
  clipEndTime: number | null | undefined;
}): Array<{ start: number; end: number }> {
  const { segments, clipStartTime, clipEndTime } = args;
  const valid: Array<{ start: number; end: number }> = [];
  if (Array.isArray(segments)) {
    for (const raw of segments) {
      if (raw === null || typeof raw !== 'object') continue;
      const { start, end } = raw as { start?: unknown; end?: unknown };
      if (isFiniteNumber(start) && isFiniteNumber(end) && end > start) valid.push({ start, end });
    }
  }
  if (valid.length > 0) return valid;
  if (isFiniteNumber(clipStartTime) && isFiniteNumber(clipEndTime) && clipEndTime > clipStartTime) {
    return [{ start: clipStartTime, end: clipEndTime }];
  }
  return [];
}

/** Where a clip's source video lives: its library for library clips, else
 *  its project (same rule as the clip cards' edit link). */
export function resolveClipSourceParent(clip: {
  is_library_clip?: boolean | null;
  project_id?: string | null;
  library_id?: string | null;
} | null | undefined): { kind: 'project' | 'library'; id: string } | null {
  if (!clip) return null;
  if (clip.is_library_clip) return clip.library_id ? { kind: 'library', id: clip.library_id } : null;
  return clip.project_id ? { kind: 'project', id: clip.project_id } : null;
}

/**
 * The ORIGINAL frame source: the project's/library's FULL source video — the
 * same full-quality URL the editor uses for cover capture
 * (`resolvePreviewVideoUrl({ quality: 'full' })`: cut, then base, then the
 * compressed tier). Its timeline is the source axis quotes live on. Never the
 * clip's own cut (`clipped_video`, a different axis) and never the render.
 * Null = capture impossible.
 */
export function resolveOriginalFrameSource(parent: {
  cut_video_url?: string | null;
  base_video_url?: string | null;
  compressed_cut_video_url?: string | null;
  compressed_base_video_url?: string | null;
} | null | undefined): string | null {
  if (!parent) return null;
  return resolvePreviewVideoUrl({ quality: 'full', project: parent }).url || null;
}

/** Well-formed `clip.data.cropCenterKeyframes` entries (source-time axis,
 *  `x` as a 0..100 percentage of the source width); anything else dropped. */
export function sanitizeCropCenterKeyframes(raw: unknown): CropCenterKeyframeLike[] {
  if (!Array.isArray(raw)) return [];
  const keyframes: CropCenterKeyframeLike[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') continue;
    const {
      time, endTime, x, y,
    } = entry as Record<string, unknown>;
    if (!isFiniteNumber(time) || !isFiniteNumber(endTime) || !isFiniteNumber(x)) continue;
    keyframes.push({
      time, endTime, x, y: isFiniteNumber(y) ? y : 50,
    });
  }
  return keyframes;
}
